// custom-category.service.ts
import { Injectable } from '@angular/core';
import { Subject, firstValueFrom } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import { ApiService } from 'src/app/services/api.service';
import { DialogService } from 'src/app/core/services/dialog.service';
import { GetBuyloadCategoryResponse } from 'src/app/core/model/server/vo/GetBuyloadCategoryResponse.interface';
import { StatusBuyloadCategoryRequest } from 'src/app/core/model/server/vo/StatusBuyloadCategoryRequest.interface';

@Injectable({
  providedIn: 'root'
})
export class CategoryService {
  
  // Observable for category changes - notifies parent components
  private categoryUpdated = new Subject<void>();
  categoryUpdated$ = this.categoryUpdated.asObservable();

  // Image validation config
  private readonly MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
  private readonly TARGET_IMAGE_SIZE = { width: 551, height: 360 };
  private readonly TARGET_BANNER_SIZE = { width: 573, height: 377 };

  constructor(
    private apiService: ApiService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private dialogService: DialogService
  ) {}

  // =============================================
  // GETTER METHODS
  // =============================================

  get maxFileSize(): number {
    return this.MAX_FILE_SIZE;
  }

  get targetImageSize(): { width: number; height: number } {
    return this.TARGET_IMAGE_SIZE;
  }

  get targetBannerSize(): { width: number; height: number } {
    return this.TARGET_BANNER_SIZE;
  }

  // =============================================
  // NOTIFICATION METHODS
  // =============================================
  
  notifyCategoryUpdate(): void {
    console.log('Notifying category update...');
    this.categoryUpdated.next();
  }

  // =============================================
  // API METHODS
  // =============================================

  async fetchCategories(
    page: number, 
    size: number, 
    params: any
  ): Promise<GetBuyloadCategoryResponse> {
    try {
      const response: GetBuyloadCategoryResponse = await firstValueFrom(
        this.apiService.getBuyloadCategories(page, size, params)
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  async saveCategory(formData: FormData): Promise<any> {
    try {
      this.spinner.show();
      const response = await this.apiService.storeCategory(formData);
      this.notifyCategoryUpdate();
      return response;
    } catch (error) {
      throw error;
    } finally {
      this.spinner.hide().catch((err) => {
        console.error('Spinner hide error:', err);
      });
    }
  }

  async updateCategory(formData: FormData): Promise<any> {
    try {
      this.spinner.show();
      const response = await this.apiService.updateCategory(formData);
      this.notifyCategoryUpdate();
      return response;
    } catch (error) {
      throw error;
    } finally {
      this.spinner.hide().catch((err) => {
        console.error('Spinner hide error:', err);
      });
    }
  }

  async bulkSaveCategories(formData: FormData): Promise<any> {
    try {
      this.spinner.show();
      const response = await this.apiService.bulkStoreCategories(formData);
      this.notifyCategoryUpdate();
      return response;
    } catch (error) {
      throw error;
    } finally {
      this.spinner.hide().catch((err) => {
        console.error('Spinner hide error:', err);
      });
    }
  }

  async toggleCategoryStatus(
    category: any, 
    action: 'enable' | 'disable'
  ): Promise<any> {
    try {
      this.spinner.show();
      
      const request: StatusBuyloadCategoryRequest = {
        itemCategory: category.itemCategory,
        action
      };
      
      const response = await firstValueFrom(
        this.apiService.setStatusCategory(request)
      );
      
      this.notifyCategoryUpdate();
      return response;
    } catch (error) {
      throw error;
    } finally {
      this.spinner.hide().catch((err) => {
        console.error('Spinner hide error:', err);
      });
    }
  }

  // =============================================
  // VALIDATION METHODS
  // =============================================

  async validateImage(
    file: File, 
    type: 'image' | 'imageBanner'
  ): Promise<{ valid: boolean; error?: string }> {
    
    // Validate file size
    if (file.size > this.MAX_FILE_SIZE) {
      const maxSizeMB = (this.MAX_FILE_SIZE / 1024 / 1024).toFixed(2);
      const currentSizeMB = (file.size / 1024 / 1024).toFixed(2);
      return {
        valid: false,
        error: `File size exceeds ${maxSizeMB}MB. Current size: ${currentSizeMB}MB`
      };
    }

    // Validate image dimensions
    try {
      const dimensions = await this.getImageDimensions(file);
      const targetSize = type === 'image' ? this.TARGET_IMAGE_SIZE : this.TARGET_BANNER_SIZE;
      
      if (dimensions.width !== targetSize.width || dimensions.height !== targetSize.height) {
        return {
          valid: false,
          error: `Image must be ${targetSize.width}x${targetSize.height}px. Current size: ${dimensions.width}x${dimensions.height}px`
        };
      }

      return { valid: true };
    } catch (error) {
      return {
        valid: false,
        error: 'Error reading image file. Please try again.'
      };
    }
  }

  // =============================================
  // FORM DATA BUILDERS
  // =============================================

  buildCategoryFormData(categoryData: any): FormData {
    const formData = new FormData();
    formData.append('itemCategory', categoryData.itemCategory);
    formData.append('categoryDescription', categoryData.categoryDescription);
    formData.append('image', categoryData.image);
    formData.append('imageBanner', categoryData.imageBanner);
    return formData;
  }

  buildUpdateFormData(category: any): FormData {
    const formData = new FormData();
    formData.append('itemCategory', category.itemCategory);
    formData.append('categoryDescription', category.categoryDescription);
    
    // Only append new images if they were uploaded
    if (category.newImage instanceof File) {
      formData.append('image', category.newImage);
    }
    
    if (category.newImageBanner instanceof File) {
      formData.append('imageBanner', category.newImageBanner);
    }
    
    return formData;
  }

  buildBulkFormData(categories: any[], images: File[], banners: File[]): FormData {
    const formData = new FormData();
    
    // Add categories as JSON
    const gamingCategories = categories.map(cat => ({
      itemCategory: cat.itemCategory,
      categoryDescription: cat.categoryDescription
    }));
    
    formData.append('gamingCategories', JSON.stringify(gamingCategories));
    
    // Add all images
    images.forEach((file) => {
      formData.append('images', file);
    });
    
    // Add all banners
    banners.forEach((file) => {
      formData.append('imageBanners', file);
    });
    
    return formData;
  }

  // =============================================
  // VALIDATION HELPER METHODS
  // =============================================

  getValidationError(control: any): string {
    if (!control || !control.errors || !control.touched) {
      return '';
    }

    if (control.errors['required']) {
      return 'This field is required';
    }
    
    if (control.errors['maxFileSize']) {
      const error = control.errors['maxFileSize'];
      return `File size exceeds ${(error.requiredSize / 1024 / 1024).toFixed(2)}MB`;
    }
    
    if (control.errors['invalidDimensions']) {
      const error = control.errors['invalidDimensions'];
      return `Image must be ${error.requiredWidth}x${error.requiredHeight}px (actual: ${error.actualWidth}x${error.actualHeight}px)`;
    }
    
    if (control.errors['invalidImage']) {
      return 'Invalid image file';
    }
    
    if (control.errors['fileReadError']) {
      return 'Error reading file';
    }
    
    return '';
  }

  // =============================================
  // UTILITY METHODS
  // =============================================

  truncateFilename(
    filename: string,
    maxLength: number = 30,
    endChars: number = 15
  ): string {
    if (!filename || filename.length <= maxLength) {
      return filename;
    }

    const lastDotIndex = filename.lastIndexOf('.');
    const extension = lastDotIndex > -1 ? filename.substring(lastDotIndex) : '';
    const nameWithoutExt = lastDotIndex > -1 ? filename.substring(0, lastDotIndex) : filename;

    const availableLength = maxLength - extension.length - 3;
    if (availableLength <= 0) {
      return '...' + extension;
    }

    const frontChars = Math.max(availableLength - endChars, 0);

    return (
      nameWithoutExt.substring(0, frontChars) +
      '...' +
      nameWithoutExt.substring(nameWithoutExt.length - endChars) +
      extension
    );
  }

  formatDate(dateString: string): string {
    if (!dateString) {
      return 'N / A';
    }
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  }

  // =============================================
  // CHANGE DETECTION METHODS
  // =============================================

  hasChanges(category: any, originalData: any): boolean {
    return originalData && (
      category.categoryDescription !== originalData.categoryDescription ||
      category.newImage instanceof File ||
      category.newImageBanner instanceof File
    );
  }

  validateFormArray(formArray: any[]): { 
    incompleteIndices: number[]; 
    invalidIndices: number[];
    hasIssues: boolean;
  } {
    const incompleteIndices: number[] = [];
    const invalidIndices: number[] = [];

    formArray.forEach((formGroup, index) => {
      const formValue = formGroup.value;
      
      // Check if all required fields are present
      const hasAllRequiredFields = 
        formValue.itemCategory && 
        formValue.categoryDescription && 
        formValue.image && 
        formValue.imageBanner;

      if (!hasAllRequiredFields) {
        incompleteIndices.push(index + 1);
        
        // Mark all fields as touched
        Object.keys(formGroup.controls).forEach(key => {
          formGroup.get(key)?.markAsTouched();
          formGroup.get(key)?.updateValueAndValidity();
        });
        return;
      }

      // Validate complete forms
      Object.keys(formGroup.controls).forEach(key => {
        formGroup.get(key)?.markAsTouched();
        formGroup.get(key)?.updateValueAndValidity();
      });

      if (formGroup.invalid) {
        invalidIndices.push(index + 1);
      }
    });

    return {
      incompleteIndices,
      invalidIndices,
      hasIssues: incompleteIndices.length > 0 || invalidIndices.length > 0
    };
  }



  // =============================================
  // IMAGE UTILITIES
  // =============================================

  private getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e: any) => {
        const img = new Image();
        
        img.onload = () => {
          resolve({ width: img.width, height: img.height });
        };
        
        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };
        
        img.src = e.target.result;
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      
      reader.readAsDataURL(file);
    });
  }
  
}
